import { signal, effect } from '@preact/signals';
import { startRecording, transcribe, level, type Recorder } from '@/lib/stt';
import { speaking, speakText, stopSpeaking } from '@/lib/tts';
import { sendMessage, setSpeaker, agentBusy } from '@/state/chat';
import { showToast } from '@/state/ui';

/** The 5-state voice machine (HANDOFF voice-button spec):
 *  idle -> listening -> processing -> (agent) -> speaking -> warm -> idle/listening */

export type VoiceState = 'idle' | 'listening' | 'processing' | 'speaking' | 'warm';

export const voiceState = signal<VoiceState>('idle');
export const micLevel = signal(0); // 0..1, drives waveform
export const warmRemaining = signal(0); // 0..1 countdown ring

export const continuousMode = signal(localStorage.getItem('tend.voice.continuous') !== '0');
export function setContinuousMode(on: boolean) {
  continuousMode.value = on;
  localStorage.setItem('tend.voice.continuous', on ? '1' : '0');
}

let recorder: Recorder | null = null;
let rafId = 0;
let warmTimer: ReturnType<typeof setInterval> | undefined;
let warmRecorder: Recorder | null = null;

// Wire chat's speak hook to TTS + machine.
setSpeaker((text) => {
  voiceEnabledThisTurn &&
    speakText(text, () => {
      if (voiceState.value === 'speaking') enterWarm();
    });
});

/** Only speak replies aloud when the question came in by voice. */
let voiceEnabledThisTurn = false;

effect(() => {
  if (speaking.value && voiceEnabledThisTurn) voiceState.value = 'speaking';
});

// agent finished without TTS (text-only or muted) — settle back to idle
effect(() => {
  if (!agentBusy.value && voiceState.value === 'processing' && !speaking.value) {
    // small delay so a speak() that's about to start wins
    setTimeout(() => {
      if (voiceState.value === 'processing' && !speaking.value) {
        if (voiceEnabledThisTurn) enterWarm();
        else voiceState.value = 'idle';
      }
    }, 600);
  }
});

function monitorLevel() {
  const buf = recorder ? new Uint8Array(recorder.analyser.frequencyBinCount) : undefined;
  const tick = () => {
    if (recorder && voiceState.value === 'listening') {
      micLevel.value = level(recorder.analyser, buf);
      rafId = requestAnimationFrame(tick);
    }
  };
  rafId = requestAnimationFrame(tick);
}

export async function startListening() {
  if (voiceState.value === 'listening') return;
  stopSpeaking();
  clearWarm();
  try {
    recorder = await startRecording();
  } catch {
    showToast({ kind: 'info', message: 'Mic unavailable — check microphone permission in Settings' }, 5000);
    voiceState.value = 'idle';
    return;
  }
  voiceState.value = 'listening';
  monitorLevel();
}

export async function stopListening() {
  if (!recorder || voiceState.value !== 'listening') return;
  cancelAnimationFrame(rafId);
  voiceState.value = 'processing';
  const rec = recorder;
  recorder = null;
  const blob = await rec.stop();

  if (blob.size < 2000) {
    // sub-quarter-second tap — nothing said
    voiceState.value = 'idle';
    return;
  }
  try {
    const text = await transcribe(blob);
    if (!text) {
      showToast({ kind: 'info', message: "Didn't catch that — try again?" });
      voiceState.value = 'idle';
      return;
    }
    voiceEnabledThisTurn = true;
    await sendMessage(text, { voice: true });
  } catch (e) {
    const msg =
      (e as Error).message === 'stt-not-configured'
        ? 'Voice transcription isn’t set up yet — type it instead.'
        : "Couldn't hear that — type it instead?";
    showToast({ kind: 'info', message: msg }, 5000);
    voiceState.value = 'idle';
  }
}

export function cancelListening() {
  cancelAnimationFrame(rafId);
  recorder?.cancel();
  recorder = null;
  clearWarm();
  stopSpeaking();
  voiceEnabledThisTurn = false;
  voiceState.value = 'idle';
}

/** Warm state: 5s window. In continuous mode the mic stays open with a
 *  simple level threshold — speak and it rolls straight into listening. */
function enterWarm() {
  voiceState.value = 'warm';
  warmRemaining.value = 1;
  const startedAt = Date.now();
  const WINDOW = 5000;

  if (continuousMode.value) {
    void startRecording()
      .then((r) => {
        if (voiceState.value !== 'warm') {
          r.cancel();
          return;
        }
        warmRecorder = r;
        const buf = new Uint8Array(r.analyser.frequencyBinCount);
        const probe = () => {
          if (voiceState.value !== 'warm' || !warmRecorder) return;
          if (level(r.analyser, buf) > 0.06) {
            // she's talking — promote the warm recorder to the live one
            recorder = warmRecorder;
            warmRecorder = null;
            clearWarm(false);
            voiceState.value = 'listening';
            monitorLevel();
            return;
          }
          requestAnimationFrame(probe);
        };
        requestAnimationFrame(probe);
      })
      .catch(() => {/* mic denied — plain warm countdown */});
  }

  warmTimer = setInterval(() => {
    const left = 1 - (Date.now() - startedAt) / WINDOW;
    warmRemaining.value = Math.max(0, left);
    if (left <= 0) {
      clearWarm();
      if (voiceState.value === 'warm') voiceState.value = 'idle';
      voiceEnabledThisTurn = false;
    }
  }, 100);
}

function clearWarm(killRecorder = true) {
  clearInterval(warmTimer);
  warmTimer = undefined;
  warmRemaining.value = 0;
  if (killRecorder && warmRecorder) {
    warmRecorder.cancel();
    warmRecorder = null;
  }
}

/** Single tap on the voice button. */
export function tapVoice() {
  switch (voiceState.value) {
    case 'idle':
      void startListening();
      break;
    case 'listening':
      void stopListening();
      break;
    case 'processing':
      break; // nothing to do — agent is thinking
    case 'speaking':
      stopSpeaking();
      enterWarm();
      break;
    case 'warm':
      clearWarm();
      void startListening();
      break;
  }
}
